export interface PdfGeneratorOptions {
  scale?: number;
  width?: number;
  backgroundColor?: string;
  fontFamily?: string;
  fontSize?: string;
  lineHeight?: string;
  color?: string;
  padding?: string;
}

const DEFAULT_OPTIONS: Required<PdfGeneratorOptions> = {
  scale: 2,
  width: 800,
  backgroundColor: '#ffffff',
  fontFamily: 'Arial, sans-serif',
  fontSize: '12px',
  lineHeight: '1.4',
  color: '#333333',
  padding: '20px',
} as const;

export class PdfGenerationError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'PdfGenerationError';
  }
}

export class HtmlToPdfGenerator {
  static async generatePdfFromHtml(htmlContent: string | Element, filename: string, options: PdfGeneratorOptions = {}): Promise<void> {
    try {
      if (!htmlContent) {
        throw new PdfGenerationError('HTML content is required');
      }
      if (!filename || typeof filename !== 'string') {
        throw new PdfGenerationError('Valid filename is required');
      }

      const normalizedFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;

      const [html2canvas, jsPDF] = await Promise.all([import('html2canvas'), import('jspdf')]);

      const opts = { ...DEFAULT_OPTIONS, ...options };

      const reportHTML = this.extractHtmlContent(htmlContent);

      const container = this.createTemporaryContainer(reportHTML, opts);
      document.body.appendChild(container);

      try {
        const canvas = await html2canvas.default(container, {
          scale: opts.scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: opts.backgroundColor,
          width: opts.width,
          height: container.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          logging: false,
        });

        const pdf = await this.createPdfFromCanvas(canvas, jsPDF.default);
        pdf.save(normalizedFilename);
      } finally {
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      }
    } catch (error) {
      console.error('PDF generation failed:', error);

      if (error instanceof PdfGenerationError) {
        throw error;
      }

      throw new PdfGenerationError('Failed to generate PDF', error);
    }
  }

  private static extractHtmlContent(htmlContent: string | Element): string {
    if (typeof htmlContent === 'string') {
      return htmlContent;
    }

    if (htmlContent instanceof Element) {
      return htmlContent.outerHTML || htmlContent.textContent || '';
    }

    throw new PdfGenerationError('Invalid HTML content type');
  }

  private static createTemporaryContainer(htmlContent: string, options: Required<PdfGeneratorOptions>): HTMLDivElement {
    const container = document.createElement('div');

    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';

    container.style.width = `${options.width}px`;
    container.style.backgroundColor = options.backgroundColor;
    container.style.padding = options.padding;
    container.style.fontFamily = options.fontFamily;
    container.style.fontSize = options.fontSize;
    container.style.lineHeight = options.lineHeight;
    container.style.color = options.color;

    container.innerHTML = htmlContent;

    return container;
  }

  private static async createPdfFromCanvas(canvas: HTMLCanvasElement, jsPDF: any): Promise<any> {
    const pdf = new jsPDF('p', 'mm', 'a4');

    const imgWidth = 210;
    const pageHeight = 295;

    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(canvas, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf;
  }
}
